import { Injectable, Injector } from '@angular/core';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { map, Observable } from 'rxjs';
import { ODataRequest } from '../utilities/sdk-base/o-data-request';
import { FactResource } from '../models/resource/fact.resource';
import { Collection } from '../models/ui/collection.model';
import { Fact } from '../models/ui/fact.model';
import { PagingResponse } from '@models/paging-response.model';
import { FactResourceToFactMapper } from '../models/mappers/fact-resource-to-fact.mapper';
import { FactToFactResourceMapper } from '../models/mappers/fact-to-fact-resource.mapper';

@Injectable({
	providedIn: 'root'
})
export class FactSdk extends SdkBase {

	constructor(injector: Injector) {
		super(injector);
	}

	public get(params?: GetFactsParams): Observable<Collection<Fact>> {
		const request = new ODataRequest('get', 'facts');

		if (params?.id) {
			request.$filter<FactResource>([{
				value: params?.id,
				operation: 'equals',
				column: 'factId'
			}]);
		}

		return this.makeODataRequest$<PagingResponse<FactResource>>(request, 'api2').pipe(
			map((response) => {
				const mapper = new FactResourceToFactMapper();
				return new Collection<Fact>({
					items: mapper.mapCollection(response.value),
					totalItems: response.count
				});
			})
		);
	}

	public post(params?: PostFactParams): Observable<Fact> {
		const factToFactResourceMapper = new FactToFactResourceMapper();
		const body = factToFactResourceMapper.map(params?.fact);
		const request = new ODataRequest('post', 'facts', body);
		return this.makeODataRequest$<FactResource>(request, 'api2').pipe(
			map((response) => {
				const factResourceToFactMapper = new FactResourceToFactMapper();
				return factResourceToFactMapper.map(response);
			})
		);
	}

	public put(params?: PutFactsParams): Observable<Fact> {
		const factToFactResourceMapper = new FactToFactResourceMapper();
		const body = factToFactResourceMapper.map(params?.fact);
		const request = new ODataRequest('put', 'facts', params.fact.id, body);
		return this.makeODataRequest$<FactResource>(request, 'api2').pipe(
			map((response) => {
				const factResourceToFactMapper = new FactResourceToFactMapper();
				return factResourceToFactMapper.map(response);
			})
		);
	}

	public delete(params?: DeleteFactParams): Observable<void> {
		const request = new ODataRequest('delete', 'facts', params.id);
		return this.makeODataRequest$(request, 'api2').pipe(
			map(() => null)
		);
	}

	public getFactFilterChoices(): Observable<Choice[]> {
		return this.makeHttpRequest<Choice[]>('get', 'api2/facts/filter-choices');
	}
}

interface GetFactsParams {
	id: string;
}

interface PostFactParams {
	fact: Fact;
}

interface PutFactsParams {
	fact: Fact;
}

interface DeleteFactParams {
	id: string;
}

export interface Choice {
    value: string;    
    displayName: string; 
}
